import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// asset_normalization_controller
//
// Provides an "Normalize with AI" button on the asset form.  On click it
// POSTs to the suggest_normalization endpoint and renders inline "chips"
// that let the user accept or dismiss each AI suggestion individually.
//
// Usage (in the asset form):
//   <div data-controller="asset-normalization"
//        data-asset-normalization-url-value="<%= suggest_normalization_company_asset_path(@company, @asset) %>">
//     <button type="button" data-action="asset-normalization#suggest"
//             data-asset-normalization-target="button">
//       Normalize with AI
//     </button>
//     <div data-asset-normalization-target="chips"></div>
//     <div data-asset-normalization-target="status"></div>
//   </div>
//
// Field mapping: the chip's data-field attribute must match the name of the
// corresponding form input (e.g. "asset[asset_type]").

export default class extends Controller {
  static targets = ["button", "chips", "status"]
  static values = { url: String }

  // Field → i18n label key (translated at render time)
  FIELD_LABEL_KEYS = {
    asset_type:   "type",
    brand:        "brand",
    model_number: "model_number",
    status:       "status",
    description:  "description",
  }

  connect() {
    this.loading = false
  }

  async suggest() {
    if (this.loading) return
    this.#setLoading(true)
    this.#clearChips()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.#csrfToken(),
          "Accept": "application/json"
        }
      })

      const data = await response.json()

      if (!response.ok) {
        this.#showStatus(data.error || t("javascript.controllers.asset_normalization_controller.could_not_fetch"), "error")
        return
      }

      const suggestions = data.suggestions || {}
      if (Object.keys(suggestions).length === 0) {
        this.#showStatus(t("javascript.controllers.asset_normalization_controller.no_suggestions"), "info")
        return
      }

      this.#renderChips(suggestions)
      this.#showStatus("", "")
    } catch (e) {
      this.#showStatus(t("javascript.controllers.asset_normalization_controller.network_error"), "error")
    } finally {
      this.#setLoading(false)
    }
  }

  // Called when a user clicks "Accept" on a chip.
  accept(event) {
    const chip = event.currentTarget.closest("[data-chip-field]")
    if (!chip) return

    const field  = chip.dataset.chipField
    const value  = chip.dataset.chipValue
    const input  = this.#findInput(field)

    if (input) {
      input.value = value
      // Fire change so other Stimulus controllers / Turbo can react
      input.dispatchEvent(new Event("change", { bubbles: true }))
      input.dispatchEvent(new Event("input",  { bubbles: true }))
    }

    chip.remove()
    if (this.chipsTarget.childElementCount === 0) {
      this.#showStatus(t("javascript.controllers.asset_normalization_controller.all_applied"), "success")
    }
  }

  // Called when a user clicks "Dismiss" on a chip.
  dismiss(event) {
    const chip = event.currentTarget.closest("[data-chip-field]")
    chip?.remove()
    if (this.chipsTarget.childElementCount === 0) {
      this.#showStatus("", "")
    }
  }

  // -- private helpers -------------------------------------------------------

  #setLoading(state) {
    this.loading = state
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = state
      this.buttonTarget.textContent = state ? t("javascript.controllers.asset_normalization_controller.analysing") : t("javascript.controllers.asset_normalization_controller.normalize_with_ai")
    }
  }

  #clearChips() {
    if (this.hasChipsTarget) this.chipsTarget.innerHTML = ""
  }

  #showStatus(message, type) {
    if (!this.hasStatusTarget) return
    const colours = { error: "text-red-400", success: "text-green-400", info: "text-gray-400" }
    this.statusTarget.className = `text-sm mt-1 ${colours[type] || "text-gray-400"}`
    this.statusTarget.textContent = message
  }

  #renderChips(suggestions) {
    const fragment = document.createDocumentFragment()

    Object.entries(suggestions).forEach(([field, value]) => {
      const labelKey = this.FIELD_LABEL_KEYS[field]
      const label = labelKey ? t(`javascript.controllers.asset_normalization_controller.field_labels.${labelKey}`) : field
      const chip  = document.createElement("div")
      chip.dataset.chipField = field
      chip.dataset.chipValue = value
      chip.className = "flex items-center gap-2 mt-2 p-2 bg-violet-900/20 border border-violet-500/40 rounded-lg text-sm"
      chip.innerHTML = `
        <span class="material-symbols-outlined text-violet-400 !text-base">auto_fix_high</span>
        <span class="text-gray-300 font-medium">${this.#escape(label)}:</span>
        <span class="text-violet-300">${this.#escape(value)}</span>
        <div class="ml-auto flex gap-1">
          <button type="button"
                  data-action="asset-normalization#accept"
                  class="px-2 py-0.5 rounded bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium transition-colors">
            ${t("javascript.controllers.asset_normalization_controller.accept")}
          </button>
          <button type="button"
                  data-action="asset-normalization#dismiss"
                  class="px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-medium transition-colors">
            ${t("javascript.controllers.asset_normalization_controller.dismiss")}
          </button>
        </div>
      `
      fragment.appendChild(chip)
    })

    this.chipsTarget.appendChild(fragment)
  }

  // Map the suggestion field name to the actual form input.
  // Tries both "asset[field]" (standard Rails form) and bare "field".
  #findInput(field) {
    return (
      document.querySelector(`[name="asset[${field}]"]`) ||
      document.querySelector(`[name="${field}"]`) ||
      document.getElementById(`asset_${field}`) ||
      null
    )
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  #escape(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
